<template>
  <router-view v-if="!isBooting" />

  <div
    v-if="!canInteract || isBooting"
    class="row justify-center items-center fixed-full z-max"
  >
    <q-spinner
      color="primary"
      size="2em"
    />
  </div>
</template>

<script setup>
import { onBeforeMount, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useQuasar } from 'quasar';
import { storeToRefs } from 'pinia';
import { useApplicationStore } from 'stores/application';
import { useAccountStore } from 'stores/account';
import { useTermStore } from 'stores/term';
import { useItsMacrosStore } from 'stores/itsmacros';
import { showErrorDialog } from 'src/lib/errorHandler';

const $q = useQuasar();
const router = useRouter();

const applicationStore = useApplicationStore();
const { isBooting, canInteract } = storeToRefs(applicationStore);

const accountStore = useAccountStore();
const { details: userDetails } = storeToRefs(accountStore);

const itsMacrosStore = useItsMacrosStore();

const termStore = useTermStore();

onBeforeMount(async () => {
  try {
    await accountStore.loadCredentials();
    itsMacrosStore.loadCredentials();
    accountStore.loadSettings();
  }
  catch (e) {
    showErrorDialog(e);
  }
  finally {
    if (!userDetails.value) {
      await router.replace({ name: 'Authentication' });
    }
    else if (!accountStore.hasSubscription) {
      await router.replace({ name: 'SubscriptionExpired' });
    }
    else {
      await router.replace({ name: 'JournalIndex' });
    }
    applicationStore.bootComplete();
  }
});

watch(userDetails, async (newValue, oldValue) => {
  if (newValue && newValue !== oldValue) {
    await termStore.searchTerms();
  }
});

// When app comes visible we need to fetch the account data
// to check if the user has subscribed.
watch(() => $q.appVisible, async (val) => {
  if (val && userDetails.value) {
    applicationStore.blockInteraction();

    try {
      await accountStore.getAccount({ id: userDetails.value.id });
      if (!accountStore.hasSubscription) {
        await router.replace({ name: 'SubscriptionExpired' });
      }
    }
    catch (e) {
      showErrorDialog(e);
    }

    applicationStore.allowInteraction();
  }
});
</script>
